
 /*
  *****************************************************************************
  * WARNING: This file is automatically generated.
  * Do not edit this file manually.
  *****************************************************************************
  * 
  * The source file is located at:
  *   /codebuild/output/src994476363/src/vl-app/pages/create-account.tsx
  */

import { createFileRoute } from '@tanstack/react-router';
import React, { ComponentProps, useEffect, useMemo, useState } from 'react';
import { Form } from 'ALIASED-antd';
import { Alert, Button } from 'antd';
import type { ErrorWithResponse } from 'store/actions/action';
import { useInitialisedStore } from '@hooks/useInitialisedStore';
import useUser from '@vl-core/hooks/useUser';
import { toast, type Toast } from 'react-hot-toast';
import { setToastError } from 'vl-common/src/lib/toastUtils';
import { useTranslation } from 'react-i18next';
import useTimingEvents, { type TimingEvent } from '@hooks/useTimingEvents';
import Header from '@components/Layouts/Header/Header';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Grid from '@components/UI/Grid';
import NavigationButtons from '@components/Register/NavigationButtons';
import Progress from '@components/Register/Progress';
import { PlusCircleOutlined } from '@ant-design/icons';
import 'react-intl-tel-input/dist/main.css';
import { TransferMessageBody, useAutoSSOWithTransferModal } from '@hooks/useAutoSSO';
import * as actions from '@actions';
import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { DateTime } from 'luxon';

import { RegistrationForm } from '@components/Patients/Registration/RegistrationForm';
import {
  transformSexFormValue,
  useRegistrationFormInitialValues
} from '@components/Patients/Registration/utils/useRegistrationFormInitialValues';
import { useRegistrationFormInstance } from '@components/Patients/Registration/useSelfRegistrationFormInstance';
import { useProfileMutation } from '@components/Patients/Registration/utils/useProfileMutation';

import { AddressFieldset } from '@components/Patients/Registration/items/AddressFieldset';
import { DateOfBirthFormItem } from '@components/Patients/Registration/items/DateOfBirthFormItem';
import { FirstNameFormItem } from '@components/Patients/Registration/items/FirstNameFormItem';
import { GPFieldset } from '@components/Patients/Registration/items/GPFieldSet';
import { LastNameFormItem } from '@components/Patients/Registration/items/LastNameFormItem';
import { MembershipNumberFormItem } from '@components/Patients/Registration/items/MembershipNumberFormItem';
import { PrimaryPhoneNumberFormItem } from '@components/Patients/Registration/items/PrimaryPhoneNumberFormItem';
import { SecondaryPhoneNumberFormItem } from '@components/Patients/Registration/items/SecondaryPhoneNumberFormItem';
import { SexFormItem } from '@components/Patients/Registration/items/SexFormItem';
import { TitleFormItem } from '@components/Patients/Registration/items/TitleFormItem';
import { TRegistrationForm } from '@components/Patients/Registration/TRegistrationForm';
import {
  RegistrationAlertProvider,
  useRegistrationAlert
} from '@components/Patients/Registration/utils/RegistrationAlertContext';
import { SsoWelcomeModal } from '@components/Patients/Registration/utils/SsoWelcomeModal';
import { RegistrationInfoBox } from '@components/Patients/Registration/utils/RegistrationInfoBox';
import { PrefNameFormItem } from '@components/Patients/Registration/items/PrefNameFormItem';
import { SetupMobile as ContactPreferenceFieldset } from './patients/register';
import i18n from 'vl-common/src/lib/i18n';

const t = i18n.getFixedT(null, 'main');

export const StyledH1 = styled.h1`
  font-weight: bold;
  font-size: 30px;
  color: var(--productGray);
  margin-bottom: 40px;

  > span {
    color: var(--productPrimary);
  }
`;

const StyledAbsoluteContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
`;

const ACCOUNT_SETUP_STEPS = {
  ACCOUNT_SETUP: 0,
  IDENTITY: 1,
  GP: 2
} as const;

const CreateAccountStepsWithoutContextProvider = ({
  allowAmendmentsToExistingFields = true,
  showSteps = true,
  onStepChange = (_s) => {},
  initialStep = ACCOUNT_SETUP_STEPS.ACCOUNT_SETUP,
  incompleteRegistration
}: {
  allowAmendmentsToExistingFields?: boolean;
  showSteps?: boolean;
  onStepChange?: (s: number) => void;
  initialStep?: number;
  incompleteRegistration?: boolean;
}) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const form = useRegistrationFormInstance();
  const user = useUser();
  const { ro_fields, sso_id } = user;
  const isMemNoReadOnly = ro_fields.includes('membership_no');
  const shouldFetchSSOData = !isMemNoReadOnly && !!sso_id;
  const readOnlyFields = [
    'title',
    'first_name',
    'last_name',
    'sex',
    'dob',
    'addline1',
    'addline2',
    'addline3',
    'addline4',
    'postcode',
    'tel_mobile'
  ];
  const showReadOnlyInfo = useMemo(() => readOnlyFields.some((field) => ro_fields.includes(field)), [ro_fields]);

  const { mutate: saveAccount } = useProfileMutation();

  const { mutateAsync: fetchSSOData, isPending: ssoDataFetching } = useMutation({
    mutationFn: async ({ sso_id, membership_no }: { sso_id: string; membership_no: string }) =>
      dispatch(actions.getSSOData(sso_id, membership_no))
  });

  const { alert, setAlert } = useRegistrationAlert();

  const [currentCreateAccountStep, setCurrentCreateAccountStep] = useState(initialStep);
  const [showSecondNumberField, setShowSecondNumberField] = useState(false);

  const startEvents: TimingEvent[] = ['IDENTITY_INFO_START', 'ADDRESS_INFO_START', 'GP_INFO_START'];
  const endEvents: TimingEvent[] = ['IDENTITY_INFO_END', 'ADDRESS_INFO_END', 'GP_INFO_END'];

  useTimingEvents(startEvents[currentCreateAccountStep], endEvents[currentCreateAccountStep]);
  useAutoSSOWithTransferModal(
    <TransferMessageBody>
      <p>{t('HBSUK just need a few personal details to get you set-up so you can book your ap')}</p>
    </TransferMessageBody>
  );

  const initialValues = useRegistrationFormInitialValues();

  useEffect(() => {
    window.localStorage.setItem('firstTimeLoginAfterAcceptingPolicy', '1');
  }, []);

  useEffect(() => {
    onStepChange(currentCreateAccountStep);
  }, [currentCreateAccountStep, onStepChange]);

  const getSSOData = async () => {
    if (!ssoDataFetching && shouldFetchSSOData && !!form.getFieldValue('mem_no')) {
      await fetchSSOData({ sso_id, membership_no: form.getFieldValue('mem_no') });
    }
  };

  const updateFormWithSSOData = (user: any) => {
    (readOnlyFields as Array<keyof TRegistrationForm>)?.forEach((field) => {
      let val = user[field];
      if (field === 'dob') {
        val = DateTime.fromISO(user[field].toISOString()).toFormat('yyyy-MM-dd');
      }

      if (field === 'sex') {
        val = transformSexFormValue(val);
      }

      form.setFieldValue(field, val);
    });
  };

  const handleAccountSetupFinish = () => {
    // We can't (necessarily) post the user profile yet because the B/E
    // considers the phone number mandatory.
    getSSOData()
      .then(async () => {
        const { user } = await dispatch(actions.userFetch());
        if (user) {
          updateFormWithSSOData(user);
        }
      })
      .catch(() => {
        console.error('Failed to load SSO data');
      })
      .finally(() => {
        setCurrentCreateAccountStep(ACCOUNT_SETUP_STEPS.IDENTITY);
      });
  };

  const steps = [
    {
      title: t('Account setup'),
      content: (
        <RegistrationForm
          form={form}
          layout="vertical"
          validateTrigger="onChange"
          initialValues={initialValues}
          onFinish={handleAccountSetupFinish}
        >
          <SsoWelcomeModal />

          <Grid columns={1}>
            <RegistrationInfoBox articleId="CONT_SELF_REG_MEMBERSHIP_NO" />
            {isMemNoReadOnly && <RegistrationInfoBox articleId="CONT_READ_ONLY_FIELD" />}
            <MembershipNumberFormItem
              inputProps={{
                disabled:
                  isMemNoReadOnly ||
                  (user.membership_no !== null && !allowAmendmentsToExistingFields) ||
                  ssoDataFetching
              }}
            />

            <RegistrationInfoBox articleId="CONT_CONTACT" />

            <ContactPreferenceFieldset hideNavigationButtons />
          </Grid>

          <NavigationButtons
            prevProps={{ disabled: true, ['data-testid']: 'previous' }}
            nextProps={{ htmlType: 'submit', loading: ssoDataFetching, ['data-testid']: 'next' }}
            data-testid="previous"
          />
        </RegistrationForm>
      )
    },
    {
      title: 'Identity',
      content: (
        <RegistrationForm
          form={form}
          layout="vertical"
          validateTrigger="onChange"
          onFinish={() =>
            saveAccount(undefined, {
              onSuccess: () => setCurrentCreateAccountStep(ACCOUNT_SETUP_STEPS.GP)
            })
          }
          initialValues={initialValues}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.target as HTMLInputElement).id === 'postcode') {
              // If Enter key is pressed on the Postcode search input
              // the default form event (moving to the next step) will be ignored.
              // Instead, an Postcode search will be performed.
              e.preventDefault();
            }
          }}
        >
          <RegistrationInfoBox articleId="CONT_IDENTITY" />
          {showReadOnlyInfo && <RegistrationInfoBox articleId="CONT_READ_ONLY_FIELD" />}
          <Grid columns={3}>
            <TitleFormItem
              selectProps={{
                disabled: ro_fields.includes('title') || (!!user.title && !allowAmendmentsToExistingFields)
              }}
            />
            <FirstNameFormItem
              inputProps={{
                disabled:
                  ro_fields.includes('first_name') || (user.first_name !== null && !allowAmendmentsToExistingFields)
              }}
            />
            <LastNameFormItem
              inputProps={{
                disabled:
                  ro_fields.includes('last_name') || (user.last_name !== null && !allowAmendmentsToExistingFields)
              }}
            />
          </Grid>

          <Grid columns={2}>
            <PrefNameFormItem
              inputProps={{
                disabled:
                  ro_fields.includes('pref_name') || (user.pref_name !== null && !allowAmendmentsToExistingFields)
              }}
            />
            <SexFormItem
              selectProps={{
                disabled: ro_fields.includes('sex') || (user.sex !== 'None' && !allowAmendmentsToExistingFields)
              }}
            />
          </Grid>

          <Grid columns={1}>
            <DateOfBirthFormItem
              inputProps={{
                disabled: ro_fields.includes('dob')
              }}
            />
            <PrimaryPhoneNumberFormItem
              inputProps={{
                disabled:
                  ro_fields.includes('tel_mobile') ||
                  form.getFieldValue('notification_preference') === 'SMS' ||
                  (!!user.tel_mobile_no && user.tel_validated)
              }}
            />
            {!showSecondNumberField && (user.tel_other_no === null || !incompleteRegistration) && (
              <Button
                css={css`
                  margin-top: -1em;
                  justify-self: end;
                  padding: 0;
                `}
                type="link"
                data-testid="add-second-number-button"
                onClick={() => setShowSecondNumberField(!showSecondNumberField)}
                icon={<PlusCircleOutlined aria-hidden="true" />}
              >
                {t('Add phone number')}
              </Button>
            )}
            {(showSecondNumberField || user.tel_other_no !== null) && (
              <SecondaryPhoneNumberFormItem inputProps={{ disabled: !!user.tel_other_no }} />
            )}
            <AddressFieldset
              inputProps={{
                disabled: ro_fields.includes('addline1') || ro_fields.includes('postcode')
              }}
              autoCompleteProps={{
                disabled: ro_fields.includes('addline1') || ro_fields.includes('postcode')
              }}
            />
          </Grid>

          <NavigationButtons
            prevProps={{
              onClick: () => setCurrentCreateAccountStep(ACCOUNT_SETUP_STEPS.ACCOUNT_SETUP),
              disabled: currentCreateAccountStep === ACCOUNT_SETUP_STEPS.ACCOUNT_SETUP && incompleteRegistration,
              ['data-testid']: 'previous'
            }}
            nextProps={{ htmlType: 'submit', ['data-testid']: 'next' }}
            data-testid="previous"
          />
        </RegistrationForm>
      )
    },
    {
      title: 'Your GP (if known)',
      content: (
        <RegistrationForm
          form={form}
          layout="vertical"
          initialValues={initialValues}
          validateTrigger="onChange"
          onFinish={() => {
            dispatch(actions.userFetch());
            saveAccount({ complete: true });
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.target as HTMLInputElement).id === 'gp_postcode') {
              // If Enter key is pressed on the GP Postcode search input
              // the default form event (moving to the next step) will be ignored.
              // Instead, an GP Postcode search will be performed.
              e.preventDefault();
            }
          }}
        >
          <RegistrationInfoBox articleId="CONT_GP" />

          <GPFieldset form={form} />
          <br />

          <NavigationButtons
            prevProps={{
              onClick: () => setCurrentCreateAccountStep(ACCOUNT_SETUP_STEPS.IDENTITY),
              ['data-testid']: 'previous'
            }}
            nextProps={{ htmlType: 'submit', children: <>{t('Create account')}</>, ['data-testid']: 'next' }}
            data-testid="navigation-buttons"
          />
        </RegistrationForm>
      )
    }
  ];

  if (currentCreateAccountStep < 0) {
    return null;
  }

  return (
    <>
      {showSteps && (
        <StyledAbsoluteContainer>
          <Progress stepNames={['Identity', 'Address', 'GP']} currentStep={currentCreateAccountStep} />
        </StyledAbsoluteContainer>
      )}

      <MyErrorBoundary>
        {alert && (
          <Alert
            data-testid="alert-message"
            className="login-alert"
            message={alert.message}
            type={alert.type}
            closable
            afterClose={() => setAlert(undefined)}
            showIcon
            css={{ marginBottom: '1rem' }}
          />
        )}
        <StyledH1>{steps[currentCreateAccountStep].title}</StyledH1>

        <div className="steps-content">{steps[currentCreateAccountStep].content}</div>
      </MyErrorBoundary>
    </>
  );
};

class MyErrorBoundary extends React.Component<{ children: React.ReactNode }> {
  static getDerivedStateFromError(_error: unknown) {
    // Update state so the next render will show the fallback UI.
    return {};
  }

  componentDidCatch(error: ErrorWithResponse) {
    delete error.stack;

    if (error?.response?.json) {
      error.response.json().then(
        (response) => {
          toast.error(
            (toast: Toast) => {
              setToastError(toast, error);
              return <p>{response.message ? response.message : t('Account creation failed')}</p>;
            },
            { duration: Infinity }
          );
        },
        () => {}
      );
      return;
    }

    console.log('Apparently is not a ErrorWithResponse');
    toast.error(<p>{t('Account creation failed')}</p>, { duration: Infinity });
  }

  render() {
    const { children } = this.props;
    return <div className="error-boundary">{children}</div>;
  }
}

const StyledSection = styled.section`
  max-width: 768px;
  padding: 64px 1em 64px;
  margin: 0 auto;
`;

export const CreateAccountSteps = (props: ComponentProps<typeof CreateAccountStepsWithoutContextProvider>) => (
  <RegistrationAlertProvider>
    <CreateAccountStepsWithoutContextProvider {...props} />
  </RegistrationAlertProvider>
);

const StyledRelativeContainer = styled.div`
  position: relative;
`;

function CreateAccount({ initialStep = ACCOUNT_SETUP_STEPS.ACCOUNT_SETUP }) {
  const initialised = useInitialisedStore();
  const { t } = useTranslation('common');

  const [form] = Form.useForm<TRegistrationForm>();
  const initialValues = useRegistrationFormInitialValues();

  if (!initialised) {
    return <p>{t('Initialising ...')}</p>;
  }

  return (
    <>
      <Header pageType="login" pageTitle={t('SelfRegistration')} />
      <StyledRelativeContainer>
        <StyledSection>
          <MyErrorBoundary>
            <Form form={form} component={false} initialValues={initialValues} preserve>
              <CreateAccountSteps initialStep={initialStep} incompleteRegistration />
            </Form>
          </MyErrorBoundary>
        </StyledSection>
      </StyledRelativeContainer>
    </>
  );
}
export default CreateAccount;
export const Route = createFileRoute('/create-account')({ component: CreateAccount });
